import { onIdTokenChanged, signOut } from "firebase/auth";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { isWardAdmin as isWardAdminFor, type WardMembership } from "~/auth/ward-roles";
import { getCurrentUser } from "~/lib/api";
import { setIdTokenGetter } from "~/lib/auth-token";
import { firebaseAuth } from "~/lib/firebase";
import { isUserRole, type UserRoleValue } from "~/lib/roles";

// The signed-in user, normalised from the Firebase user record and the ID
// token's custom claims (`role`, written by the API at registration time).
export interface AuthUser {
  uid: string;
  email: string | null;
  name: string | null;
  role: UserRoleValue | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  idToken: string | null;
  isLoading: boolean;
  logout: () => Promise<void>;
  /** Global super-admin flag, from `GET /auth/me` (not the Firebase token). */
  isSuperAdmin: boolean;
  /** The signed-in user's ward memberships, from `GET /auth/me`. */
  wards: WardMembership[];
  /** Whether the user administers the given ward. */
  isWardAdmin: (wardId: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Register the token getter the generated API client calls on every request.
// Done at module load (not in an effect) so requests fired before <AuthProvider>
// has mounted still resolve a token. Firebase caches the token in memory and
// refreshes it transparently, so this stays cheap.
setIdTokenGetter(() => firebaseAuth.currentUser?.getIdToken() ?? Promise.resolve(null));

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Server-side access context (super-admin flag + per-ward roles). These live
  // in the app DB, not the Firebase token, so they're loaded from /auth/me.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [wards, setWards] = useState<WardMembership[]>([]);

  useEffect(() => {
    // onIdTokenChanged fires on sign-in, sign-out, AND silent token refreshes,
    // so the context's token stays current without polling. The first fire also
    // ends the initial loading state once Firebase has hydrated from storage.
    const unsubscribe = onIdTokenChanged(firebaseAuth, async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setIdToken(null);
        setIsSuperAdmin(false);
        setWards([]);
        setIsLoading(false);
        return;
      }
      try {
        // getIdTokenResult (no force-refresh) reads the cached token + its
        // decoded claims — no network round-trip, no risk of re-triggering
        // this handler.
        const result = await fbUser.getIdTokenResult();
        setUser({
          uid: fbUser.uid,
          email: fbUser.email,
          name: fbUser.displayName,
          role: isUserRole(result.claims.role) ? result.claims.role : null,
        });
        setIdToken(result.token);
        // Resolve the v2 access context from the API. Wrapped in its own catch
        // so a /auth/me failure degrades to "no admin access" rather than
        // signing the (validly authenticated) user out.
        try {
          const me = await getCurrentUser();
          setIsSuperAdmin(me.data?.isSuperAdmin ?? false);
          setWards(me.data?.wards ?? []);
        } catch {
          setIsSuperAdmin(false);
          setWards([]);
        }
      } catch {
        // Token resolution failed (transient error, revoked refresh token).
        // Treat as signed-out rather than hanging the app on the spinner.
        setUser(null);
        setIdToken(null);
        setIsSuperAdmin(false);
        setWards([]);
      } finally {
        // Always clear the initial loading state, success or failure.
        setIsLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    // signOut triggers onIdTokenChanged(null) above, which clears user +
    // idToken. Future global app state (stores, caches) should be reset here.
    await signOut(firebaseAuth);
  }, []);

  const isWardAdmin = useCallback(
    (wardId: string): boolean => isWardAdminFor(wards, wardId),
    [wards],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, idToken, isLoading, logout, isSuperAdmin, wards, isWardAdmin }),
    [user, idToken, isLoading, logout, isSuperAdmin, wards, isWardAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
